import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantsAPI, customersAPI } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CountrySelect } from '../components/CountrySelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Plus, Search, Users, ChevronRight, Loader2, AlertCircle, PowerOff, Power, Trash2, Shield, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

const NAVY   = 'hsl(var(--c4p-navy-900))';
const CURRENCIES = ['GBP', 'EUR', 'USD', 'CAD', 'AUD'];
const defaultForm = { company_name: '', contact_name: '', email: '', phone_mobile: '', website: '', billing_address: '', country: '', default_currency: 'GBP', tenant_type: 'standard', num_users: '', partner_id: '', partner_name: '', notes: '' };

export default function TenantsPage() {
  const { isManager, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants]   = useState([]);
  const [partners, setPartners] = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [typeFilter, setTypeFilter]     = useState('');   // '' | 'standard' | 'mtpro'
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]     = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sortBy, setSortBy] = useState('company_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const PAGE_SIZE = 20;

  useEffect(() => {
    customersAPI.list({ page_size: 200, status: 'active' }).then(r => setPartners(r.data.items || [])).catch(() => {});
  }, []);

  const handleSort = (field) => {
    if (sortBy === field) { setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc'); }
    else { setSortBy(field); setSortOrder('asc'); }
    setPage(1);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, status: statusFilter, sort_by: sortBy, sort_order: sortOrder };
      if (search) params.search = search;
      if (partnerFilter) params.partner_id = partnerFilter;
      if (typeFilter) params.tenant_type = typeFilter;
      const { data } = await tenantsAPI.list(params);
      setTenants(data.items || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load tenants'); }
    finally { setLoading(false); }
  }, [page, search, statusFilter, partnerFilter, typeFilter, sortBy, sortOrder]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault(); setErr(null); setSaving(true);
    try {
      const partner = partners.find(p => p.id === form.partner_id);
      await tenantsAPI.create({ ...form, partner_name: partner?.company_name || null, partner_id: form.partner_id || null });
      toast.success(`${form.company_name} created`);
      setShowCreate(false); setForm(defaultForm); load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Failed to create tenant'); }
    finally { setSaving(false); }
  };

  const quickToggleStatus = async (e, t) => {
    e.stopPropagation();
    try {
      await tenantsAPI.update(t.id, { status: t.status === 'active' ? 'inactive' : 'active' });
      toast.success(`${t.company_name} ${t.status === 'active' ? 'deactivated' : 'activated'}`);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await tenantsAPI.delete(deleteTarget.id); toast.success('Tenant deleted'); setDeleteTarget(null); load(); }
    catch { toast.error('Delete failed'); }
    finally { setDeleting(false); }
  };

  const canManage = isAdmin || isManager;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-5" data-testid="tenants-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>Tenants</h2>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
            {isManager ? 'All tenants' : 'Your tenants'} &middot; {total} total
          </p>
        </div>
        <Button onClick={() => navigate('/tenants/new')} style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }} className="gap-2">
          <Plus size={15} /> New Tenant
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'hsl(var(--muted-fg))' }} />
          <Input placeholder="Search tenants..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
        </div>
        <Select value={partnerFilter || 'all'} onValueChange={v => { setPartnerFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All partners" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All partners</SelectItem>
            {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter || 'all'} onValueChange={v => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="mtpro">MTPro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'hsl(var(--muted))' }}>
              {[['company_name','Company'],['partner_name','Partner'],['country','Country']].map(([f,l]) => {
                const active = sortBy === f;
                return (
                  <TableHead key={f} className="cursor-pointer select-none hover:bg-muted/30"
                    onClick={() => handleSort(f)} data-testid={`sort-${f}`}>
                    <div className="flex items-center gap-1">{l}
                      {active ? (sortOrder === 'desc' ? <span style={{ fontSize:10,opacity:.7 }}>↓</span> : <span style={{ fontSize:10,opacity:.7 }}>↑</span>) : <span style={{ fontSize:10,opacity:.3 }}>↕</span>}
                    </div>
                  </TableHead>
                );
              })}
              <TableHead>Type</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead className="text-center">MSA</TableHead>
              <TableHead className="text-center">Doc</TableHead>
              <TableHead>Status</TableHead>
              {canManage && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}><TableCell colSpan={10}><Skeleton className="h-10 w-full" /></TableCell></TableRow>
            )) : tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-12">
                  <Users size={28} className="mx-auto mb-2" style={{ color: 'hsl(var(--border))' }} />
                  <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No tenants found.</p>
                </TableCell>
              </TableRow>
            ) : tenants.map(t => (
              <TableRow key={t.id} className="data-table-row cursor-pointer" style={{ opacity: t.status === 'active' ? 1 : 0.55 }}
                onClick={() => navigate(`/tenants/${t.id}`)}>
                <TableCell>
                  <p className="font-semibold text-sm">{t.company_name}</p>
                </TableCell>
                <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{t.partner_name || '\u2014'}</TableCell>
                <TableCell className="text-sm">{t.country || '\u2014'}</TableCell>
                <TableCell>
                  {t.tenant_type === 'mtpro' ? (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'hsl(265 89% 95%)', color: 'hsl(265 89% 45%)' }}>
                      MTPro{t.num_users > 0 ? ` · ${t.num_users} users` : ''}
                    </span>
                  ) : (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-fg))' }}>Standard</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{t.contact_name || '\u2014'}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs font-mono">{t.default_currency}</Badge></TableCell>
                <TableCell className="text-center">
                  {t.msa_signed ? (
                    <span title="MSA recorded as signed" data-testid={`tenant-msa-signed-indicator-${t.id}`}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                      style={{ background: '#dcfce7' }}>
                      <Shield size={12} style={{ color: '#15803d' }} />
                    </span>
                  ) : (
                    <span title="MSA not signed" data-testid={`tenant-msa-unsigned-indicator-${t.id}`}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                      style={{ background: '#fef2f2' }}>
                      <Shield size={12} style={{ color: '#dc2626' }} />
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {t.msa_document ? (
                    <span title={t.msa_document.filename} data-testid={`tenant-msa-doc-indicator-${t.id}`}
                      className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                      style={{ background: '#f0fdf4' }}>
                      <Paperclip size={12} style={{ color: '#15803d' }} />
                    </span>
                  ) : (
                    <span className="text-sm" style={{ color: 'hsl(var(--border))' }}>&mdash;</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs font-medium"
                    style={t.status === 'active' ? { background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' } : { background: '#f8fafc', color: '#475569', borderColor: '#e2e8f0' }}>
                    {t.status === 'active' ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                {canManage && (
                  <TableCell>
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={e => quickToggleStatus(e, t)}
                        style={{ color: t.status === 'active' ? '#d97706' : '#15803d' }}>
                        {t.status === 'active' ? <PowerOff size={13} /> : <Power size={13} />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setDeleteTarget(t); }}
                        style={{ color: 'hsl(var(--danger))' }}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: 'hsl(var(--muted-fg))' }}>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Tenant</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {err && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>}
            <div className="space-y-1.5"><Label>Company Name *</Label><Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} required /></div>
            {/* Tenant type */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Tenant Type</Label>
                <Select value={form.tenant_type} onValueChange={v => setForm(f => ({ ...f, tenant_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="mtpro">MTPro (Multi-Tenant)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.tenant_type === 'mtpro' && (
                <div className="space-y-1.5"><Label>User Count</Label>
                  <Input type="number" min="0" placeholder="0" value={form.num_users}
                    onChange={e => setForm(f => ({ ...f, num_users: e.target.value }))} />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact Name</Label><Input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>Website</Label><Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Country</Label>
                <CountrySelect value={form.country || ''} onChange={v => setForm(f => ({ ...f, country: v }))} testId="tenant-country" />
              </div>
              <div className="space-y-1.5"><Label>Currency</Label>
                <Select value={form.default_currency} onValueChange={v => setForm(f => ({ ...f, default_currency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Linked Partner</Label>
              <Select value={form.partner_id || 'none'} onValueChange={v => setForm(f => ({ ...f, partner_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="No linked partner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked partner</SelectItem>
                  {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Billing Address</Label><Input value={form.billing_address} onChange={e => setForm(f => ({ ...f, billing_address: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Notes</Label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full rounded-md border px-3 py-2 text-sm h-16 resize-none" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ background: NAVY, color: 'white' }}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Tenant'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertCircle size={16} style={{ color: 'hsl(var(--danger))' }} /> Delete Tenant</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>Delete <strong>{deleteTarget?.company_name}</strong>? Existing quotes will not be affected.</p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} style={{ background: 'hsl(var(--danger))', color: 'white' }}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
